import React, { useEffect, useState } from "react";
import {
  Building2,
  ChevronDown,
  ChevronUp,
  MoreVertical,
  Pencil,
  Plus,
  Power,
  Search,
  SlidersHorizontal,
  Upload,
  X,
} from "lucide-react";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import TableV2 from "src/components/ui/TableV2";
import { Badge } from "src/components/ui/Badge";
import CardHeader from "src/components/common/card/CardHeader";
import Button from "src/components/ui/Button";
import Input from "src/components/ui/Input";
import FilterSelect from "src/components/ui/FilterSelect";
import PageSizeSelect from "src/components/ui/PageSizeSelect";
import Card from "src/components/common/card/Card";
import Popover from "src/components/ui/Popover";
import ConfirmationModal from "src/components/modals/ConfirmationModal";
import AddSupplierModal from "src/components/modals/AddSupplierModal";
import BulkImportModal, {
  BulkImportColumn,
} from "src/components/bulk-import/BulkImportModal";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as supplierActions from "src/redux/slices/supplierSlice";
import { Supplier } from "src/types/supplier.types";
import { toast } from "react-toastify";
import { formatAddress } from "src/utils/formatAddress";
import supplierApis from "src/api/supplier";

const STATUS_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const SUPPLIER_IMPORT_COLUMNS: BulkImportColumn[] = [
  { key: "name", label: "Name", required: true, example: "Global Textiles Pvt Ltd" },
  { key: "contactPerson", label: "Contact Person", example: "Ramesh Kumar" },
  { key: "email", label: "Email", example: "ramesh@globaltextiles.com" },
  { key: "phone", label: "Phone", example: "9876543210" },
  { key: "gstNumber", label: "GST Number", example: "29ABCDE1234F1Z5" },
  { key: "addressLine1", label: "Address Line 1", required: true, example: "12 Industrial Area" },
  { key: "addressLine2", label: "Address Line 2", example: "Phase 2" },
  { key: "city", label: "City", example: "Bengaluru" },
  { key: "state", label: "State", example: "Karnataka" },
  { key: "pincode", label: "Pincode", example: "560001" },
  { key: "status", label: "Status", example: "active" },
];

const SupplierListPage: React.FC = () => {
  const [limit, setLimit] = useState(10);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState<Supplier | null>(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusRow, setStatusRow] = useState<Supplier | null>(null);
  const [statusUpdate, setStatusUpdate] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"" | "active" | "inactive">("");

  const { supplierList, editSupplier } = useAppSelector(
    (state) => state.supplier
  );
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const permissions = user?.roleDetails?.permissions || user?.permissions || {};
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";
  const canCreateSupplier =
    isTenantAdmin || (permissions?.suppliers || []).includes("create");
  const canEditSupplier =
    isTenantAdmin || (permissions?.suppliers || []).includes("edit");
  const canToggleSupplier =
    isTenantAdmin || (permissions?.suppliers || []).includes("delete");
  const dispatch = useAppDispatch();

  const rows: Supplier[] = supplierList?.data?.data || [];

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    getSuppliers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status, limit]);

  useEffect(() => {
    if (editSupplier.data && statusUpdate) {
      toast.success("Supplier modified successfully");
      dispatch(supplierActions.resetEditSupplier());
      setStatusUpdate(false);
      handleStatusModalClose();
      getSuppliers(supplierList?.data?.pagination?.currentPage || 1);
    } else if (editSupplier.error && statusUpdate) {
      toast.error(`Error: ${editSupplier.error}`);
      setStatusUpdate(false);
      handleStatusModalClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editSupplier, statusUpdate, dispatch]);

  const getSuppliers = (page: number) =>
    dispatch(
      supplierActions.getSupplierListRequest({
        page,
        limit,
        ...(search ? { search } : {}),
        ...(status ? { status } : {}),
      })
    );

  const handlePageChange = (page: number) => getSuppliers(page);

  const handleClearFilters = () => {
    setSearchInput("");
    setSearch("");
    setStatus("");
  };
  const hasActiveFilters = !!(search || status);

  const handleModalClose = () => {
    setOpenModal(false);
    setSelectedRow(null);
  };

  const handleAddSupplierSuccess = () => {
    setOpenModal(false);
    setSelectedRow(null);
    getSuppliers(supplierList?.data?.pagination?.currentPage || 1);
  };

  const handleEdit = (data: Supplier) => {
    setSelectedRow(data);
    setOpenModal(true);
  };

  const handleStatusModalClose = () => {
    setShowStatusModal(false);
    setStatusRow(null);
  };

  const onConfirmStatus = () => {
    if (!statusRow?._id) return;
    setStatusUpdate(true);
    dispatch(
      supplierActions.editSupplierRequest({
        _id: statusRow._id,
        status: statusRow.status === "active" ? "inactive" : "active",
      })
    );
  };

  return (
    <DashboardLayout
      meta={{
        title: "Suppliers | Invyntra",
        description:
          "Manage and oversee all suppliers associated with your tenant.",
        keywords: "suppliers, management, invyntra",
      }}
    >
      <div className="space-y-4">
        {/* Page header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
              <Building2 size={26} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Suppliers
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Supplier directory and contacts.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {canCreateSupplier && (
              <Button
                variant="outline"
                rounded="full"
                onClick={() => setShowImportModal(true)}
              >
                <Upload size={16} className="mr-1.5" />
                Bulk import
              </Button>
            )}
            {canCreateSupplier && (
              <Button rounded="full" onClick={() => setOpenModal(true)}>
                <Plus size={16} className="mr-1" />
                Add Supplier
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card>
          <div className="flex w-full items-center justify-between">
            <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <SlidersHorizontal size={14} />
              Filters
              {hasActiveFilters && (
                <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
              )}
            </span>
            <Button
              variant="outline"
              size="sm"
              rounded="full"
              onClick={() => setFiltersOpen((o) => !o)}
            >
              {filtersOpen ? "Hide filters" : "Show filters"}
              {filtersOpen ? (
                <ChevronUp size={16} className="ml-1.5" />
              ) : (
                <ChevronDown size={16} className="ml-1.5" />
              )}
            </Button>
          </div>

          {filtersOpen && (
            <div className="mt-4 grid grid-cols-1 gap-4 border-t border-gray-100 pt-4 dark:border-gray-700 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Search
                </label>
                <Input
                  placeholder="Search suppliers..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  startAdornment={<Search size={14} />}
                />
              </div>

              <FilterSelect
                variant="boxed"
                label="Status"
                value={status}
                onChange={(val) => setStatus(val as "" | "active" | "inactive")}
                allLabel="All statuses"
                options={STATUS_OPTIONS}
              />

              <div className="flex items-end">
                <Button
                  variant="outline"
                  color="danger"
                  rounded="full"
                  fullWidth
                  onClick={handleClearFilters}
                  disabled={!hasActiveFilters}
                >
                  <X size={15} className="mr-1.5" />
                  Clear filters
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* List */}
        <Card>
          <CardHeader
            title="Suppliers"
            description="Search and manage supplier records."
            actions={<PageSizeSelect value={limit} onChange={setLimit} />}
          />

          <TableV2
            columns={[
              {
                key: "supplierId",
                header: "#ID",
                accessor: ({ row, key }) => (
                  <span className="text-gray-500">#{row[key]}</span>
                ),
              },
              {
                key: "name",
                header: "Supplier",
                accessor: ({ row }) => (
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {row.name}
                    </div>
                    {row?.contactPerson && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {row.contactPerson}
                      </p>
                    )}
                  </div>
                ),
              },
              { key: "phone", header: "Phone" },
              { key: "email", header: "Email" },
              {
                key: "gstNumber",
                header: "GST Number",
                accessor: ({ row }) => row.gstNumber || "-",
              },
              {
                key: "addressLine1",
                header: "Address",
                width: "250px",
                accessor: ({ row }) => formatAddress(row) || "-",
              },
              {
                key: "status",
                header: "Status",
                accessor: ({ row }) => (
                  <Badge
                    text={row.status === "active" ? "Active" : "Inactive"}
                    color={row.status === "active" ? "green" : "gray"}
                  />
                ),
              },
              {
                key: "actions",
                header: "Action",
                accessor: ({ row }) =>
                  !canEditSupplier && !canToggleSupplier ? null : (
                    <Popover
                      trigger={
                        <button
                          className="p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
                          title="Actions"
                        >
                          <MoreVertical size={16} />
                        </button>
                      }
                    >
                      {(close) => (
                        <div className="w-44 py-1.5">
                          {canEditSupplier && (
                            <button
                              className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                              onClick={() => {
                                close();
                                handleEdit(row);
                              }}
                            >
                              <Pencil size={14} />
                              Edit
                            </button>
                          )}
                          {canToggleSupplier && (
                            <button
                              className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                              onClick={() => {
                                close();
                                setStatusRow(row);
                                setShowStatusModal(true);
                              }}
                            >
                              <Power size={14} />
                              {row.status === "active" ? "Deactivate" : "Activate"}
                            </button>
                          )}
                        </div>
                      )}
                    </Popover>
                  ),
              },
            ]}
            data={rows}
            pagination={supplierList?.data?.pagination}
            onPageChange={handlePageChange}
            loading={supplierList.loading}
          />
        </Card>
      </div>

      {openModal && (
        <AddSupplierModal
          open={openModal}
          supplier={selectedRow}
          onClose={handleModalClose}
          onSuccess={handleAddSupplierSuccess}
        />
      )}

      {showImportModal && (
        <BulkImportModal
          open={showImportModal}
          onClose={() => setShowImportModal(false)}
          title="Bulk import suppliers"
          entityLabel="suppliers"
          columns={SUPPLIER_IMPORT_COLUMNS}
          sampleFileName="supplier-import-sample.xlsx"
          onImport={(rows) => supplierApis.bulkImport(rows)}
          onImported={() => getSuppliers(1)}
        />
      )}

      {showStatusModal && statusRow?._id && (
        <ConfirmationModal
          title={
            statusRow.status === "active"
              ? "Deactivate supplier"
              : "Activate supplier"
          }
          message={`Are you sure you want to ${
            statusRow.status === "active" ? "deactivate" : "activate"
          } "${statusRow.name}"?`}
          onConfirm={onConfirmStatus}
          onCancel={handleStatusModalClose}
          open={showStatusModal}
          isLoading={editSupplier.loading}
        />
      )}
    </DashboardLayout>
  );
};

export default SupplierListPage;
